import React, { useState, useEffect } from 'react';
import {
  Modal,
  Box,
  Typography,
  Button,
  Checkbox,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
  Chip,
  CircularProgress,
} from '@mui/material';
import { Close, Reorder } from '@mui/icons-material';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import axios from "axios";

const ExamSelectionModal = ({ open, onClose, onConfirm, schoolId, grade, currentExam }) => {
  const [availableExams, setAvailableExams] = useState([]);
  const [selectedExams, setSelectedExams] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && schoolId && grade) {
      fetchAvailableExams();
    }
  }, [open, schoolId, grade]);

  const fetchAvailableExams = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `https://ed-9ny1.onrender.com/api/schools/${schoolId}/performance/available-exams`,
        { params: { class: grade } }
      );
      
      // Filter out current exam and sort by date
      const filteredExams = response.data
        .filter(exam => 
          !(exam.term === currentExam.term && 
            exam.examType === currentExam.examType && 
            exam.examName === currentExam.examName)
        )
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      
      // Add unique IDs if they don't exist
      const examsWithIds = filteredExams.map(exam => ({
        ...exam,
        uniqueId: exam._id || `${exam.term}-${exam.examType}-${exam.examName || 'main'}-${Date.now()}`
      }));
      
      setAvailableExams(examsWithIds);
    } catch (error) {
      console.error('Error fetching available exams:', error);
    } finally {
      setLoading(false);
    }
  };

  // FIXED: Proper checkbox toggle function
  const handleToggleExam = (examToToggle) => {
    const isSelected = selectedExams.some(
      selected => selected.uniqueId === examToToggle.uniqueId
    );

    if (isSelected) {
      // Remove from selected exams
      setSelectedExams(selectedExams.filter(
        selected => selected.uniqueId !== examToToggle.uniqueId
      ));
    } else {
      // Add to selected exams
      setSelectedExams([...selectedExams, examToToggle]);
    }
  };

  // FIXED: Proper drag and drop handler
  const handleDragEnd = (result) => {
    if (!result.destination) return;

    const items = Array.from(selectedExams);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    setSelectedExams(items);
  };

  const handleConfirm = () => {
    onConfirm(selectedExams);
    onClose();
  };

  const getExamDisplayName = (exam) => {
    if (exam.examName && exam.examName !== 'Unnamed') {
      return `${exam.examType}: ${exam.examName}`;
    }
    return exam.examType;
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: 600,
        bgcolor: 'background.paper',
        boxShadow: 24,
        p: 4,
        borderRadius: 2,
        maxHeight: '80vh',
        overflow: 'auto'
      }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h6" component="h2">
            Select Previous Exams for Comparison
          </Typography>
          <IconButton onClick={onClose}>
            <Close />
          </IconButton>
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Current Exam: {getExamDisplayName(currentExam)} (Term {currentExam.term})
        </Typography>

        {/* Available Exams */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="subtitle1" sx={{ mb: 2 }}>
            Available Exams ({availableExams.length})
          </Typography>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : (
            <List>
              {availableExams.map((exam) => (
                <ListItem 
                  key={exam.uniqueId} 
                  dense
                  sx={{
                    border: '1px solid',
                    borderColor: 'grey.200',
                    borderRadius: 1,
                    mb: 1,
                    bgcolor: selectedExams.some(selected => selected.uniqueId === exam.uniqueId) 
                      ? '#f0f9ff' 
                      : 'background.paper'
                  }}
                >
                  <ListItemIcon>
                    <Checkbox
                      edge="start"
                      checked={selectedExams.some(selected => selected.uniqueId === exam.uniqueId)}
                      onChange={() => handleToggleExam(exam)}
                      inputProps={{ 'aria-labelledby': `exam-${exam.uniqueId}` }}
                    />
                  </ListItemIcon>
                  <ListItemText
                    id={`exam-${exam.uniqueId}`}
                    primary={getExamDisplayName(exam)}
                    secondary={`Term ${exam.term} • ${new Date(exam.createdAt).toLocaleDateString()} • ${exam.studentCount || 0} students`}
                  />
                  <Chip 
                    label={exam.term} 
                    size="small" 
                    variant="outlined"
                    sx={{ mr: 1 }}
                  />
                </ListItem>
              ))}
            </List>
          )}
        </Paper>

        {/* Selected Exams in Order */}
        {selectedExams.length > 0 && (
          <Paper sx={{ p: 2, mb: 3 }}>
            <Typography variant="subtitle1" sx={{ mb: 2 }}>
              Selected Exams (Drag to reorder - most recent first)
            </Typography>
            <DragDropContext onDragEnd={handleDragEnd}>
              <Droppable droppableId="selected-exams">
                {(provided, snapshot) => (
                  <List 
                    {...provided.droppableProps} 
                    ref={provided.innerRef}
                    sx={{
                      backgroundColor: snapshot.isDraggingOver ? '#f8f9fa' : 'transparent',
                      minHeight: 100,
                      transition: 'background-color 0.2s ease'
                    }}
                  >
                    {selectedExams.map((exam, index) => (
                      <Draggable 
                        key={exam.uniqueId} 
                        draggableId={exam.uniqueId} 
                        index={index}
                      >
                        {(provided, snapshot) => (
                          <ListItem
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                            sx={{
                              border: '1px solid',
                              borderColor: snapshot.isDragging ? '#3182CE' : 'grey.300',
                              borderRadius: 1,
                              mb: 1,
                              bgcolor: snapshot.isDragging ? '#e6f7ff' : 'background.paper',
                              boxShadow: snapshot.isDragging ? 2 : 0,
                              transform: snapshot.isDragging ? 'rotate(2deg)' : 'none',
                              transition: 'all 0.2s ease'
                            }}
                          >
                            <ListItemIcon>
                              <Reorder />
                            </ListItemIcon>
                            <ListItemText
                              primary={`${index + 1}. ${getExamDisplayName(exam)}`}
                              secondary={`Term ${exam.term}`}
                            />
                            <Chip 
                              label={`#${index + 1}`} 
                              size="small" 
                              color="primary"
                            />
                            <IconButton 
                              size="small" 
                              onClick={() => handleToggleExam(exam)}
                              sx={{ ml: 1 }}
                            >
                              <Close fontSize="small" />
                            </IconButton>
                          </ListItem>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </List>
                )}
              </Droppable>
            </DragDropContext>
          </Paper>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
          <Button onClick={onClose} variant="outlined">
            Cancel
          </Button>
          <Button 
            onClick={handleConfirm} 
            variant="contained" 
            disabled={selectedExams.length === 0}
          >
            Confirm Selection ({selectedExams.length} exams)
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default ExamSelectionModal;